import { BaseOption } from "./form";

export type MultilingualOption = BaseOption & {
  multilingual: boolean;
};

export const getFlagIcon = (languageCode: string): string => {
  return new URL(`/src/assets/icons/flags/${languageCode}.svg`, import.meta.url)
    .href;
};

const MULTILINGUAL_OPTIONS: MultilingualOption[] = [
  {
    value: "auto",
    label: "Multilingual Mode (AI-detected)",
    sublabel1: "AI detects any mix of these languages:",
    sublabel2:
      "Spanish, German, Dutch, Hindi, Russian, Japanese, Italian, English",
    multilingual: true,
  },
];

const NON_MULTILINGUAL_OPTIONS: MultilingualOption[] = [
  // Reference order
  // English & Spanish
  { value: "en", label: "English", sublabel1: "(Default)", multilingual: false },
  { value: "es", label: "Spanish", sublabel1: "(Español)", multilingual: false },
  // European
  { value: "fr", label: "French", sublabel1: "(Français)", multilingual: false },
  { value: "de", label: "German", sublabel1: "(Deutsch)", multilingual: false },
  { value: "it", label: "Italian", sublabel1: "(Italiano)", multilingual: false },
  { value: "pt", label: "Portuguese", sublabel1: "(Português)", multilingual: false },
  { value: "nl", label: "Dutch", sublabel1: "(Nederlands)", multilingual: false },
  { value: "ru", label: "Russian", sublabel1: "(Русский)", multilingual: false },
  { value: "pl", label: "Polish", sublabel1: "(Polski)", multilingual: false },
  { value: "sv", label: "Swedish", sublabel1: "(Svenska)", multilingual: false },
  { value: "bg", label: "Bulgarian", sublabel1: "(Български)", multilingual: false },
  { value: "ro", label: "Romanian", sublabel1: "(Română)", multilingual: false },
  { value: "cs", label: "Czech", sublabel1: "(Čeština)", multilingual: false },
  { value: "el", label: "Greek", sublabel1: "(Ελληνικά)", multilingual: false },
  { value: "fi", label: "Finnish", sublabel1: "(Suomi)", multilingual: false },
  { value: "hr", label: "Croatian", sublabel1: "(Hrvatski)", multilingual: false },
  { value: "sk", label: "Slovak", sublabel1: "(Slovenčina)", multilingual: false },
  { value: "da", label: "Danish", sublabel1: "(Dansk)", multilingual: false },
  { value: "uk", label: "Ukrainian", sublabel1: "(Українська)", multilingual: false },
  { value: "hu", label: "Hungarian", sublabel1: "(Magyar)", multilingual: false },
  { value: "no", label: "Norwegian", sublabel1: "(Norsk)", multilingual: false },
  // Asian
  { value: "hi", label: "Hindi", sublabel1: "(हिन्दी)", multilingual: false },
  { value: "te", label: "Telugu", sublabel1: "(తెలుగు)", multilingual: false },
  { value: "mr", label: "Marathi", sublabel1: "(मराठी)", multilingual: false },
  { value: "zh", label: "Chinese", sublabel1: "(中文)", multilingual: false },
  { value: "ja", label: "Japanese", sublabel1: "(日本語)", multilingual: false },
  { value: "ko", label: "Korean", sublabel1: "(한국어)", multilingual: false },
  { value: "ta", label: "Tamil", sublabel1: "(தமிழ்)", multilingual: false },
  { value: "bn", label: "Bengali", sublabel1: "(বাংলা)", multilingual: false },
  { value: "ur", label: "Urdu", sublabel1: "(اردو)", multilingual: false },
  { value: "vi", label: "Vietnamese", sublabel1: "(Tiếng Việt)", multilingual: false },
  { value: "id", label: "Indonesian", sublabel1: "(Indonesia)", multilingual: false },
  { value: "th", label: "Thai", sublabel1: "(ไทย)", multilingual: false },
  { value: "ms", label: "Malay", sublabel1: "(Bahasa Melayu)", multilingual: false },
  { value: "tl", label: "Tagalog", sublabel1: "(Tagalog)", multilingual: false },
  { value: "gu", label: "Gujarati", sublabel1: "(ગુજરાતી)", multilingual: false },
  { value: "kn", label: "Kannada", sublabel1: "(ಕನ್ನಡ)", multilingual: false },
  { value: "ml", label: "Malayalam", sublabel1: "(മലയാളം)", multilingual: false },
  { value: "pa", label: "Punjabi", sublabel1: "(Pañjābī)", multilingual: false },
  // Other
  { value: "ar", label: "Arabic", sublabel1: "(العربية)", multilingual: false },
  { value: "tr", label: "Turkish", sublabel1: "(Türkçe)", multilingual: false },
  { value: "he", label: "Hebrew", sublabel1: "(עברית)", multilingual: false },
  { value: "ka", label: "Georgian", sublabel1: "(ქართული)", multilingual: false },
];

// Languages supported by the pysbd sentence boundary library on the backend.
// This list gates which languages appear in the language selector.
//
// NOTE: Some languages (e.g. "te" Telugu) are NOT natively supported by pysbd
// but are included here because the backend falls back to English sentence
// boundary rules (see backend/pipeline/tts/buffers.py TextBuffer.__init__).
// This works for languages that use standard punctuation (. ! ?) for sentence
// endings. If adding a language with non-standard sentence boundaries, verify
// TTS chunking quality before adding it here.
const PYSBD_SUPPORTED_LANGUAGES = [
  "en",
  "mr",
  "hi",
  "bg",
  "es",
  "ru",
  "ar",
  "am",
  "hy",
  "fa",
  "ur",
  "pl",
  "zh",
  "nl",
  "da",
  "fr",
  "it",
  "el",
  "my",
  "ja",
  "de",
  "kk",
  "te",
];

const NON_MULTILINGUAL_OPTIONS_WITH_ICONS: MultilingualOption[] =
  NON_MULTILINGUAL_OPTIONS.map((option) => ({
    ...option,
    icon: getFlagIcon(option.value),
  }));

const LANGUAGE_OPTIONS = [
  ...MULTILINGUAL_OPTIONS,
  ...NON_MULTILINGUAL_OPTIONS_WITH_ICONS.filter((option) =>
    PYSBD_SUPPORTED_LANGUAGES.includes(option.value),
  ),
];

export default LANGUAGE_OPTIONS;
